import { useMemo } from 'react'
import { useCompanyLocation } from 'hooks/useCompanyLocation'
import { useCompanyInfo } from 'queries/company'
import { useIsDrivingPosition } from './pages/DetailsPage/useIsDrivingPosition'
import { useShiftEditor } from './useShiftEditor'
import { Location } from 'typings/common_defs'

interface CaliforniaDrivingShiftInputs {
  isDrivingPosition: boolean
  location: Location | null | undefined
  isW2: boolean | undefined
}

/**
 * Pure predicate: a shift is a California driving shift when it is a 1099
 * on-demand delivery block performed by a driving position at a California
 * location. Use this directly from places that hold live, uncommitted form
 * state (e.g. DetailsPage); use the hook variant for read-only consumers of
 * the committed shiftData (e.g. ConfirmPage).
 */
export function getIsCaliforniaDrivingShift({
  isDrivingPosition,
  location,
  isW2,
}: CaliforniaDrivingShiftInputs) {
  return isDrivingPosition && location?.address?.state === 'CA' && !isW2
}

/**
 * Returns true when the shift being edited is a California 1099 on-demand
 * delivery block performed by a driving position. Reads all inputs from the
 * committed `shiftData` in the ShiftEditor context.
 */
export function useIsCaliforniaDrivingShift() {
  const { shiftData, isOnDemandDeliveryBlock } = useShiftEditor()
  const location = useCompanyLocation(shiftData?.details?.locationId)
  const isDrivingPosition = useIsDrivingPosition(
    shiftData?.details?.positionId ?? 0
  )
  const { data: company } = useCompanyInfo()

  return useMemo(
    () =>
      getIsCaliforniaDrivingShift({
        isDrivingPosition,
        location,
        isW2: company?.w2,
      }),
    [isOnDemandDeliveryBlock, isDrivingPosition, location, company]
  )
}
